import React, { useEffect, useRef, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLayout } from '../context/LayoutContext';
import Modal from './Modal';
import './Sidebar.css';

interface MenuItem {
  label: string;
  path: string;
}

interface SubItem {
  label: string;
  tab: string;
}

/** A top-level item that expands into its page's sub-tabs. */
interface SubmenuSection {
  items: SubItem[];
  /** Tab the page lands on when ?tab= is absent, for active-link matching. */
  defaultTab: string;
}

const FILE_DRIVE_SUBITEMS: SubItem[] = [
  { label: 'Personal', tab: 'personal' },
  { label: 'Shared', tab: 'shared' },
  { label: 'Archived', tab: 'archived' },
];

const INVENTORY_SUBITEMS: SubItem[] = [
  { label: 'Inventory', tab: 'inventory' },
  { label: 'Purchase Order', tab: 'purchase-order' },
  { label: 'Log', tab: 'log' },
];

const INSTALLATION_SUBITEMS: SubItem[] = [
  { label: 'Service and Support', tab: 'service-support' },
  { label: 'Calendar', tab: 'calendar' },
  { label: 'Site Inspection', tab: 'site-inspection' },
  { label: 'Installation & Deployment', tab: 'deployment' },
  { label: 'Customer Sign-Offs', tab: 'sign-offs' },
];

const Sidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const layout = useLayout();
  const { user } = useAuth();
  const isOpen = layout?.mobileMenuOpen ?? false;
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  const closeMobileMenuRef = useRef(layout?.closeMobileMenu);
  closeMobileMenuRef.current = layout?.closeMobileMenu;

  const isAdmin = Array.isArray(user?.roles) && user!.roles.includes('admin');
  const hasMarketing = Array.isArray(user?.roles) && user!.roles.includes('marketing');

  const submenuSections: Record<string, SubmenuSection> = useMemo(() => {
    const marketingItems: SubItem[] = [{ label: 'Marketing Order Form', tab: 'order-form' }];
    // The lead inboxes are marketing's own tools; the order form is open to
    // the whole team.
    if (isAdmin || hasMarketing) {
      marketingItems.push({ label: 'Website Form Leads', tab: 'website-leads' });
      marketingItems.push({ label: 'Robotics Proposal', tab: 'robotics-proposal' });
      marketingItems.push({ label: 'Hospitality', tab: 'hospitality' });
      marketingItems.push({ label: 'Product Inquiries', tab: 'product-inquiries' });
      marketingItems.push({ label: 'Intake Form Responses', tab: 'intake-responses' });
      marketingItems.push({ label: 'Business Card Scanner', tab: 'card-scanner' });
      marketingItems.push({ label: 'Campaigns', tab: 'campaigns' });
    }
    return {
      '/installation': { items: INSTALLATION_SUBITEMS, defaultTab: 'calendar' },
      '/inventory': { items: INVENTORY_SUBITEMS, defaultTab: 'inventory' },
      '/marketing': { items: marketingItems, defaultTab: 'order-form' },
      '/file-drive': { items: FILE_DRIVE_SUBITEMS, defaultTab: 'personal' },
    };
  }, [isAdmin, hasMarketing]);

  const currentTab = new URLSearchParams(location.search).get('tab');
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});

  /* Keep a submenu expanded whenever the user is inside that section. */
  const sectionPaths = Object.keys(submenuSections).join(',');
  useEffect(() => {
    const inSection = sectionPaths
      .split(',')
      .filter((p) => p && location.pathname.startsWith(p));
    if (inSection.length === 0) return;
    setOpenMenus((prev) => {
      const next = { ...prev };
      for (const p of inSection) next[p] = true;
      return next;
    });
  }, [location.pathname, sectionPaths]);

  /* Close menu only when route changes (user navigated), not when context reference changes */
  useEffect(() => {
    closeMobileMenuRef.current?.();
  }, [location.pathname]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') layout?.closeMobileMenu();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, layout]);

  const mainMenuItems: MenuItem[] = useMemo(() => {
    const items: MenuItem[] = [
      { label: 'Dashboard', path: '/dashboard' },
      { label: 'My Tasks', path: '/tasks' },
      { label: 'Tasks Board', path: '/projects' },
      { label: 'Client', path: '/client' },
      { label: 'Orders', path: '/orders' },
      { label: 'Installation', path: '/installation' },
      { label: 'Elevators', path: '/elevators' },
      // Open to everyone: the marketing order form is a team-wide request form.
      // The Website Form Leads table inside is still marketing/admin only.
      { label: 'Marketing', path: '/marketing' },
      { label: 'Events', path: '/events' },
      { label: 'Inventory', path: '/inventory' },
      { label: 'Robots', path: '/robots' },
      { label: 'File Drive', path: '/file-drive' },
      { label: 'Submit Feedback', path: '/feedback' },
    ];
    if (isAdmin) {
      items.push({ label: 'Employees', path: '/employees' });
    }
    return items;
  }, [isAdmin]);

  const settingsMenuItem: MenuItem = { label: 'Settings', path: '/settings' };

  const isActive = (path: string) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/';
    }
    if (path === '/client') {
      return location.pathname.startsWith('/client') || location.pathname.startsWith('/lead');
    }
    if (path === '/feedback') {
      return location.pathname === '/feedback';
    }
    if (path === '/projects') {
      return location.pathname === '/projects' || location.pathname.startsWith('/projects/');
    }
    if (path === '/events') {
      return location.pathname === '/events' || location.pathname.startsWith('/events/');
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      <div className="mobile-topbar">
        <button
          type="button"
          className="mobile-topbar-toggle"
          onClick={layout?.toggleMobileMenu}
          aria-label="Open menu"
          aria-expanded={isOpen}
        >
          ☰
        </button>
      </div>

      {isOpen && (
        <div
          className="sidebar-overlay"
          onClick={layout?.closeMobileMenu}
          aria-hidden="true"
        />
      )}
      <aside className={`sidebar ${isOpen ? 'sidebar--open' : ''}`}>
        <div className="sidebar-brand">
          <img src="/images/TechForceLogo-KO+Green-Horizontal.svg" alt="TechForce Robotics" className="sidebar-brand-logo" />
        </div>
        <nav className="sidebar-nav">
          <ul className="sidebar-menu">
            {mainMenuItems.map((item) => {
              const section = submenuSections[item.path];
              if (section) {
                const inSection = location.pathname.startsWith(item.path);
                const activeSubTab = currentTab || section.defaultTab;
                const expanded = openMenus[item.path] ?? false;
                return (
                  <li key={item.path} className="sidebar-menu-item sidebar-has-submenu">
                    <div className="sidebar-parent-row">
                      <Link to={item.path} className="sidebar-link sidebar-parent-link">
                        {item.label}
                      </Link>
                      <button
                        type="button"
                        className={`sidebar-submenu-toggle ${expanded ? 'sidebar-submenu-toggle--open' : ''}`}
                        aria-label={`${expanded ? 'Collapse' : 'Expand'} ${item.label} submenu`}
                        aria-expanded={expanded}
                        onClick={() =>
                          setOpenMenus((prev) => ({ ...prev, [item.path]: !expanded }))
                        }
                      >
                        <span className="sidebar-submenu-caret" aria-hidden="true">▸</span>
                      </button>
                    </div>
                    {expanded && (
                      <ul className="sidebar-submenu">
                        {section.items.map((sub) => (
                          <li key={sub.tab} className="sidebar-menu-item">
                            <Link
                              to={`${item.path}?tab=${sub.tab}`}
                              className={`sidebar-link sidebar-sublink ${
                                inSection && activeSubTab === sub.tab ? 'active' : ''
                              }`}
                            >
                              {sub.label}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              }
              return (
                <li key={item.path} className="sidebar-menu-item">
                  <Link
                    to={item.path}
                    className={`sidebar-link ${isActive(item.path) ? 'active' : ''}`}
                  >
                    {item.label}
                  </Link>
                </li>
              );
            })}
          </ul>

          <ul className="sidebar-menu sidebar-menu-bottom">
            <li className="sidebar-menu-item">
              <Link
                to={settingsMenuItem.path}
                className={`sidebar-link ${isActive(settingsMenuItem.path) ? 'active' : ''}`}
              >
                {settingsMenuItem.label}
              </Link>
            </li>
            <li className="sidebar-menu-item">
              <button type="button" className="sidebar-logout" onClick={() => setConfirmOpen(true)}>
                Log out
              </button>
            </li>
          </ul>
        </nav>
      </aside>

      <Modal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Log out"
        narrow
      >
        <div className="modal-body">
          <p style={{ margin: '0 0 1.5rem', color: '#374151', fontSize: '0.9375rem' }}>
            Are you sure you want to log out?
          </p>
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" onClick={() => setConfirmOpen(false)}>
            Cancel
          </button>
          <button type="button" className="sidebar-logout-confirm-btn" onClick={handleLogout}>
            Log out
          </button>
        </div>
      </Modal>
    </>
  );
};

export default Sidebar;


